import type { Omraade, Tilgang, Medlemsrolle, Hjelperolle, Niva } from '../api/types';

/**
 * Speiling av rollemodellen i API-et.
 *
 * Serveren er den som bestemmer – dette eksemplaret avgjør bare hva
 * grensesnittet viser. Skjuler vi et verktøy serveren ville sluppet gjennom,
 * ser det ut som en feil; viser vi en knapp serveren avviser, er det verre.
 */

export const HJELPEROLLER: readonly Hjelperolle[] = ['toastmaster', 'forlover'];
export const OMRAADER: readonly Omraade[] = [
  'guests',
  'tasks',
  'budget',
  'seating',
  'communication',
];

export const NAVN_PAA_ROLLE: Record<Hjelperolle, string> = {
  toastmaster: 'Toastmaster',
  forlover: 'Forlover',
};

export const NAVN_PAA_OMRAADE: Record<Omraade, string> = {
  guests: 'Gjesteliste',
  tasks: 'Oppgaver',
  budget: 'Budsjett',
  seating: 'Bordkart',
  communication: 'Kommunikasjon',
};

/** Én linje som forklarer hva hvert område faktisk gir tilgang til. */
export const HVA_OMRAADET_GIR: Record<Omraade, string> = {
  guests: 'Navn, kontaktinfo og allergier på gjestene',
  tasks: 'Sjekklista fram mot dagen',
  budget: 'Hva dere har satt av, og hva dere har brukt',
  seating: 'Bordene og hvem som sitter hvor',
  communication: 'Å sende e-post til gjestene i deres navn',
};

export const FULL_TILGANG: Tilgang = {
  guests: 'write',
  tasks: 'write',
  budget: 'write',
  seating: 'write',
  communication: 'write',
};

export const INGEN_TILGANG: Tilgang = {
  guests: null,
  tasks: null,
  budget: null,
  seating: null,
  communication: null,
};

export function erBrudeparet(rolle: Medlemsrolle | string): boolean {
  return rolle === 'owner' || rolle === 'partner';
}

/**
 * Rangen nivåene sammenlignes på. Høyere rommer alt lavere.
 *
 * `egen` ligger mellom lese og skrive og finnes bare på kommunikasjon: å sende
 * praktiske beskjeder til gjestene er en stor del av å være toastmaster, mens
 * invitasjonen og påminnelsen er brudeparets egen stemme.
 */
const RANG: Record<Exclude<Niva, null> | 'ingen', number> = {
  ingen: 0,
  read: 1,
  egen: 2,
  write: 3,
};

/** Sant når nivået rekker til det som kreves. Høyere nivå rommer lavere. */
export function rekker(har: Niva, krever: 'read' | 'egen' | 'write'): boolean {
  return RANG[har ?? 'ingen'] >= RANG[krever];
}

const VANLIGE_NIVAER: { verdi: Niva; navn: string }[] = [
  { verdi: null, navn: 'Ingenting' },
  { verdi: 'read', navn: 'Kan se' },
  { verdi: 'write', navn: 'Kan endre' },
];

/**
 * Valgene et område kan settes til, med ord brudeparet kan lese.
 *
 * Per område og ikke én felles liste: kommunikasjon har fire nivåer, resten
 * har tre, og en tabell med et hull i ville vært vanskeligere å forstå enn en
 * rad som sier hva den betyr.
 */
export const NIVAVALG: Record<Omraade, { verdi: Niva; navn: string }[]> = {
  guests: VANLIGE_NIVAER,
  tasks: VANLIGE_NIVAER,
  budget: VANLIGE_NIVAER,
  seating: VANLIGE_NIVAER,
  communication: [
    { verdi: null, navn: 'Ingenting' },
    { verdi: 'read', navn: 'Kan se hva som er sendt' },
    { verdi: 'egen', navn: 'Kan sende egne meldinger' },
    { verdi: 'write', navn: 'Kan sende alt, også invitasjonen' },
  ],
};

/**
 * Tilgangen til det aktive bryllupet, med et fullt kart som reserve.
 *
 * Reserven gjelder gamle svar fra API-et som ennå ikke har feltet. Å anta full
 * tilgang der er trygt fordi serveren uansett avviser – alternativet, å anta
 * ingenting, ville tømt menyen for brudeparet ved en tilfeldig gammel kapsel.
 */
export function tilgangenTil(
  bryllup: { role: string; tilgang?: Tilgang; delingPaaPause?: boolean } | null,
): Tilgang {
  if (!bryllup) return FULL_TILGANG;
  if (erBrudeparet(bryllup.role)) return FULL_TILGANG;
  // Før reserven: et bryllup på pause skal aldri gi en hjelper full meny.
  if (bryllup.delingPaaPause) return INGEN_TILGANG;
  return bryllup.tilgang ?? FULL_TILGANG;
}

/**
 * Er delingen med den innloggede satt på pause?
 *
 * Toastmaster og forlover er en del av Plus. Har bryllupet ikke Plus lenger,
 * ser de ingenting – men rollen og det brudeparet har delt, blir liggende.
 * API-et regner det ut og sender det med; dette er bare oppslaget.
 */
export function delingErPaaPause(bryllup: { delingPaaPause?: boolean } | null): boolean {
  return bryllup?.delingPaaPause === true;
}

/**
 * Hva et bryllup er for den som ser på det.
 *
 * Skiller ens eget fra dem man hjelper til i. Uten den forskjellen ser en
 * liste over tre bryllup like ut, og det er ikke likegyldig hvilket man
 * åpner – i ett av dem er man brudeparet.
 */
export function rolleEtikett(rolle: string, paaPause = false): string {
  if (rolle === 'owner' || rolle === 'partner') return 'Ditt eget bryllup';
  const etikett =
    rolle === 'toastmaster'
      ? 'Du er toastmaster'
      : rolle === 'forlover'
        ? 'Du er forlover'
        : 'Du hjelper til';
  // Står i lista man velger fra, så man vet det før man åpner bryllupet.
  return paaPause ? `${etikett} · på pause` : etikett;
}
